import {
  EDesignerEntityKind,
  ESdChangeReason,
  IWorkFlowDto,
  LeftPanelType,
  RightPanelType
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { WorkFlowService } from "../../../services/workflow-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { SEARCH_DEBOUNCE_MS } from "../../../shared/hooks/useDebouncedValue";
import { PureSdComponent } from "../../../shared/pure-sd";
import { FeatureGates } from "../../../shared/utility/feature-gates";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import {
  hasRecentlyModified,
  RECENTLY_MODIFIED_TAB,
  RECENTLY_MODIFIED_TAB_TEST_ID,
  resolveActiveTab,
  selectRecentlyModified
} from "../../../shared/utility/recently-modified";
import {
  createFeedRequestGuard,
  haveItemIdsChanged,
  loadModificationFeed,
  LOADING_FEED,
  type IFeedRequestGuard,
  type IModificationFeed
} from "../../../shared/utility/recently-modified-feed";
import {
  buildDefaultWorkflowJson,
  buildDefaultWorkflowXml,
  findWorkflowNameConflict,
  validateWorkflowName,
  workflowNameConflictMessage
} from "../../../shared/utility/workflow-templates";
import { DeleteModal } from "../../common/delete-modal";
import KuikaEmptyState from "../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInfoBox from "../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaListPage, {
  KuikaCreateCard,
  KuikaCreateCardRow,
  KuikaListPageHeader,
  KuikaListPageToolbar
} from "../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaListTable, {
  KuikaListTableColumn,
  KuikaListTableNameCell,
  KuikaListTableRowAction,
  KuikaListTableStatusCell,
  KuikaListTableUserCell
} from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSkeleton from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import {
  filterProcesses,
  getProcessCreatorInitials,
  getProcessTimestamp,
  IProcessSortOption,
  isProcessActive,
  PROCESS_SORT_OPTIONS,
  ProcessSortKey,
  processTestIdSlug,
  ProcessStatusFilter,
  resolveProcessRename,
  type ProcessRenameOutcome
} from "./create-workflow-processes";
import "./create-workflow.scss";
import CreateWorkflowTemplateCard from "./create-workflow-template-card";
import CreateWorkflowTemplateDetailsModal from "./create-workflow-template-details-modal";
import {
  collectProcessTags,
  filterProcessTemplates,
  IProcessContent,
  IProcessTemplate,
  ITemplateSortOption,
  personalizeTemplateContent,
  TEMPLATE_SORT_OPTIONS,
  TemplateSortKey,
  toProcessTemplates
} from "./create-workflow-templates";
import { buildProcessRowActions, deleteProcessEverywhere, toggleProcessActive } from "./process-row-actions";
import { describeWorkflowListSummary, formatWorkflowFlowCell, IWorkflowListSummary } from "./workflow-list-summary";

/** The tab holding every process, beside the "Recently modified" one the page opens on. */
const ALL_TAB: string = "All Processes";
const TEMPLATES_TAB: string = "Process Templates";

/** Fallback name for "Create a blank"; suffixed with the first free index. */
const BLANK_PROCESS_BASE_NAME = "New Process";
const BLANK_PROCESS_MAX_ATTEMPTS = 200;

const STATUS_OPTIONS: KuikaSelectboxOption[] = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" }
];

interface ILoadProcessesOptions {
  /**
   * Refetch without the table spinner: the rows stay on screen and update in place.
   * Used after a mutation, where blanking the whole table for a one-row change reads
   * as a flash rather than as progress.
   */
  silent?: boolean;
}

export interface ICreateWorkflowState {
  isCreating: boolean;
  /**
   * The template whose process `isCreating` is for; undefined for a blank process. Keys the
   * spinner to the card that was pressed rather than to every card, and to the blank card.
   */
  creatingTemplateId?: string;
  isProcessesLoading: boolean;
  /** A silent refetch is in flight — drives `aria-busy` and guards re-entry. */
  isRefreshing: boolean;
  /** Ids of rows with a mutation in flight; each shows a spinner on its own row. */
  busyProcessIds: string[];
  processes: IWorkFlowDto[];
  /** The backend template catalog; loaded lazily on the tab's first activation. */
  templates: IProcessTemplate[];
  isTemplatesLoading: boolean;
  /** Set after the first (attempted) load so a tab revisit doesn't refetch. */
  templatesLoaded: boolean;
  /** The tab the user chose, or null while the page is still deciding for itself. */
  pickedTab: string | null;
  feed: IModificationFeed;
  /** The search input's live value; every keystroke echoes here. */
  search: string;
  /** The debounced copy of `search` that actually drives filtering. */
  searchQuery: string;
  statusFilter: ProcessStatusFilter | null;
  processSort: ProcessSortKey | null;
  templateTag: string | null;
  templateSort: TemplateSortKey | null;
  processToDelete?: IWorkFlowDto;
  /** Template whose details modal is open; `undefined` keeps it closed. */
  templateToView?: IProcessTemplate;
  /** Row whose name cell is the inline rename editor; null keeps every cell plain. */
  renamingProcessId: string | null;
  renameValue: string;
  /** A rename PUT is in flight — blocks the Enter-then-blur double commit. */
  isRenameSaving: boolean;
}

export interface ICreateWorkflowProps {
  editingWfId?: string;
}

class CreateWorkflow extends PureSdComponent<ICreateWorkflowProps & IWithSDProps, ICreateWorkflowState> {
  private isMounted_: boolean = false;
  /** Guards `loadFeed` against an out-of-order reply when the item set changes twice in a row. */
  private feedGuard_: IFeedRequestGuard = createFeedRequestGuard();

  constructor(props: ICreateWorkflowProps & IWithSDProps) {
    super(props);
    this.state = {
      isCreating: false,
      isProcessesLoading: true,
      isRefreshing: false,
      busyProcessIds: [],
      processes: [],
      templates: [],
      isTemplatesLoading: false,
      templatesLoaded: false,
      pickedTab: null,
      feed: LOADING_FEED,
      search: "",
      searchQuery: "",
      statusFilter: null,
      processSort: null,
      templateTag: null,
      templateSort: null,
      renamingProcessId: null,
      renameValue: "",
      isRenameSaving: false
    };
  }

  componentDidMount(): void {
    this.isMounted_ = true;
    void this.loadProcesses();
    void this.loadFeed();
  }

  componentWillUnmount(): void {
    this.isMounted_ = false;
    this.applySearchQuery.cancel();
  }

  /**
   * `processes` is this page's OWN fetch (`loadProcesses`), not a prop, because the provider's
   * `workFlowList` drops fields the table needs — so the reload signal watches state rather than
   * props here. `loadProcesses({ silent: true })` already runs after every create, rename, and
   * delete (`refreshAfterMutation`), which is what keeps this in sync without polling.
   *
   * `prevState.isProcessesLoading` guards against the one update this must NOT react to: the
   * initial fetch landing (`[]` → the real list), which `componentDidMount` already asked the feed
   * for directly. `isProcessesLoading` is true only while that first, non-silent fetch is in
   * flight — every later refresh is silent and leaves it false — so it is what tells the initial
   * list apart from a genuine change without a second flag.
   */
  componentDidUpdate(_prevProps: Readonly<ICreateWorkflowProps & IWithSDProps>, prevState: ICreateWorkflowState): void {
    if (prevState.isProcessesLoading) return;
    if (haveItemIdsChanged(prevState.processes, this.state.processes, (row: IWorkFlowDto) => row.id)) {
      void this.loadFeed();
    }
  }

  /**
   * `IWorkFlowDto` does carry `updatedAt` and its writers — the Last Updated column reads
   * them — but which processes are "recent" is the feed's answer, the one every
   * other landing reads, so the eight tabs agree on what the word means.
   */
  loadFeed = async (): Promise<void> => {
    const token: number = this.feedGuard_.start();
    const projectId: string | undefined = this.props.sd?.project?.id;
    const feed: IModificationFeed = await loadModificationFeed([EDesignerEntityKind.WorkFlow], projectId);
    if (!this.isMounted_ || !this.feedGuard_.isCurrent(token)) return;
    this.setState({ feed });
  };

  /**
   * Derived rather than stored, so it cannot go stale against the feed: the page opens on the
   * recent tab, drops to All Processes when nothing is recent or the feed failed, and otherwise
   * shows whatever the user picked.
   */
  getActiveTab = (): string =>
    resolveActiveTab({
      pickedTab: this.state.pickedTab,
      feedStatus: this.state.feed.status,
      // Asked of every process, not the searched or status-filtered ones: a query that matches
      // nothing recent must not move the user to another tab mid-keystroke.
      hasRecent: hasRecentlyModified<IWorkFlowDto>(
        this.state.processes,
        this.state.feed.index,
        (row: IWorkFlowDto) => row.id,
        Date.now()
      ),
      // This page fetches its own rows, so the feed can answer first. Deciding then would read
      // "nothing is recent" off the still-empty list, drop to All Processes, and jump back once
      // the processes land.
      itemsReady: !this.state.isProcessesLoading,
      recentTab: RECENTLY_MODIFIED_TAB,
      fallbackTab: ALL_TAB
    });

  /**
   * Which processes belong on the recent tab is the feed's answer, and so is their order —
   * unless the user picked a Sort-by. That control is shown on this tab, and the tab is the one
   * the page opens on, so it must not turn into a dead control here.
   */
  getRecentProcesses = (rows: IWorkFlowDto[]): IWorkFlowDto[] => {
    const recent: IWorkFlowDto[] = selectRecentlyModified<IWorkFlowDto>({
      items: rows,
      index: this.state.feed.index,
      getId: (row: IWorkFlowDto) => row.id,
      getName: (row: IWorkFlowDto) => row.displayName || row.name || "",
      now: Date.now()
    });
    if (this.state.processSort === null) return recent;
    const recentIds: Set<string> = new Set<string>(recent.map((row: IWorkFlowDto): string => row.id));
    return rows.filter((row: IWorkFlowDto): boolean => recentIds.has(row.id));
  };

  /**
   * Debounced bridge from the input to the filter: `search` updates per keystroke (the
   * input stays live), `searchQuery` follows once typing pauses so the table/grid is not
   * re-filtered on every key.
   */
  private applySearchQuery = _.debounce((query: string): void => {
    this.setState({ searchQuery: query });
  }, SEARCH_DEBOUNCE_MS);

  handleSearchChange = (value: string): void => {
    this.setState({ search: value });
    this.applySearchQuery(value);
  };

  /**
   * Same endpoint the (now hidden) Processes panel uses, called directly so the
   * table gets `IWorkFlowDto` — the provider's `workFlowList` is `IWorkFlow`, which
   * drops `createdAt` and would leave the date columns with nothing to render.
   */
  loadProcesses = async ({ silent = false }: ILoadProcessesOptions = {}): Promise<void> => {
    const projectId: string | undefined = this.props.sd.project?.id;
    if (!projectId) {
      this.setState({ isProcessesLoading: false, isRefreshing: false });
      return;
    }
    try {
      if (silent) {
        this.setState({ isRefreshing: true });
      } else {
        this.setState({ isProcessesLoading: true });
      }
      const res = await WorkFlowService.GetAllByProjectId(projectId);
      if (res?.status === 200 && Array.isArray(res.data)) {
        this.setState({ processes: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isProcessesLoading: false, isRefreshing: false });
    }
  };

  markProcessBusy = (processId: string, busy: boolean): void => {
    this.setState((prev: ICreateWorkflowState): Pick<ICreateWorkflowState, "busyProcessIds"> => ({
      busyProcessIds: busy
        ? [...prev.busyProcessIds, processId]
        : prev.busyProcessIds.filter((id: string): boolean => id !== processId)
    }));
  };

  isProcessBusy = (process: IWorkFlowDto): boolean => this.state.busyProcessIds.includes(process.id);

  /**
   * Refreshes the table AND the provider list, so the rest of the app agrees. Silent
   * on purpose: the row the user just acted on stays visible and updates in place.
   */
  refreshAfterMutation = async (): Promise<void> => {
    const projectId: string | undefined = this.props.sd.project?.id;
    await this.loadProcesses({ silent: true });
    if (projectId) await this.props.sd.loadWorkFlowList(projectId);
  };

  /**
   * The single creation path: validate → conflict-check → build the content (the default
   * BPMN skeleton, or a template's json/xml pair) → create → select the new process and
   * leave the gallery.
   */
  createProcess = async (name: string, template?: IProcessTemplate): Promise<void> => {
    try {
      this.setState({ isCreating: true, creatingTemplateId: template?.id });
      const invalid: string | null = validateWorkflowName(name);
      if (invalid) {
        this.setState({ isCreating: false });
        KMainFunctions.displayWarningNotification(invalid);
        return;
      }

      const conflict = findWorkflowNameConflict({
        name,
        workflows: this.props.sd.workFlowList,
        screens: this.props.sd?.screenList,
        datasources: this.props.sd?.datasources,
        excludeWorkflowId: this.props.editingWfId
      });
      if (conflict) {
        this.setState({ isCreating: false });
        KMainFunctions.displayWarningNotification(workflowNameConflictMessage(conflict));
        return;
      }

      const content: IProcessContent = template
        ? personalizeTemplateContent(template, name)
        : { json: buildDefaultWorkflowJson(name), xml: buildDefaultWorkflowXml(name) };

      const obj: IWorkFlowDto = {
        id: Guid.create().toString(),
        name,
        displayName: name,
        description: template?.description ?? "",
        kProject_Id: this.props.sd.project.id,
        json: content.json,
        xml: content.xml
      };
      const res = await WorkFlowService.Create(obj);
      if (res && res.status === 200) {
        this.props.sd.clearSelectedWf();
        await this.props.sd.loadWorkFlowList(this.props.sd.project.id);
        await this.props.sd.setSelectedWf(res.data);
        this.props.sd.setActiveWorkFlowLeftPanel(LeftPanelType.WorkFlowFieldList);
        this.props.sd.setActiveWorkFlowRightPanel(RightPanelType.Properties);
        this.props.sd.setCreatingWfActive(false);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isCreating: false, creatingTemplateId: undefined });
    }
  };

  /**
   * The gallery has no name field, so a blank process is auto-named. Picks the first
   * index that collides with no existing process, screen, or datasource.
   */
  buildBlankProcessName = (): string => {
    for (let index = 1; index <= BLANK_PROCESS_MAX_ATTEMPTS; index++) {
      const candidate: string = `${BLANK_PROCESS_BASE_NAME} ${index}`;
      const conflict = findWorkflowNameConflict({
        name: candidate,
        workflows: this.props.sd.workFlowList,
        screens: this.props.sd?.screenList,
        datasources: this.props.sd?.datasources,
        excludeWorkflowId: this.props.editingWfId
      });
      if (!conflict) return candidate;
    }
    // Exhausted: hand back the base candidate and let createProcess surface the
    // conflict, rather than silently inventing an unpredictable name.
    return `${BLANK_PROCESS_BASE_NAME} 1`;
  };

  handleCreateBlank = (): void => {
    if (this.state.isCreating) return;
    void this.createProcess(this.buildBlankProcessName());
  };

  /** Opening a row leaves the gallery and hands the process to the modeler. */
  handleOpenProcess = (process: IWorkFlowDto): void => {
    // While the rename editor is open, a row click is the click that blurs (and commits)
    // the editor — letting it also open a process would race the rename's save.
    if (this.state.renamingProcessId) return;
    this.props.sd.setActiveWorkFlowLeftPanel(LeftPanelType.WorkFlowFieldList);
    this.props.sd.setActiveWorkFlowRightPanel(RightPanelType.Properties);
    this.props.sd.setSelectedWf(process.id);
    this.props.sd.setCreatingWfActive(false);
  };

  handleToggleProcessStatus = async (process: IWorkFlowDto): Promise<void> => {
    // Nothing spins during a silent refresh, so the menu stays clickable — guard the
    // window between the request and the refreshed rows landing.
    if (this.state.isRefreshing || this.isProcessBusy(process)) return;
    this.markProcessBusy(process.id, true);
    try {
      await toggleProcessActive(process);
      await this.refreshAfterMutation();
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.markProcessBusy(process.id, false);
    }
  };

  // --- Inline rename ---------------------------------------------------------
  // The rename the retired Processes panel offered, on the row it now lives on. The
  // rules are the shared workflow-name rules (see resolveProcessRename), the save is
  // the same partial PUT the panel sent: `{ name }` only, so json/xml stay untouched
  // and the backend maps it onto DisplayName.

  startRename = (process: IWorkFlowDto): void => {
    if (this.isProcessBusy(process)) return;
    this.setState({ renamingProcessId: process.id, renameValue: process.displayName || process.name });
  };

  cancelRename = (): void => {
    this.setState({ renamingProcessId: null, renameValue: "" });
  };

  commitRename = async (): Promise<void> => {
    const processId: string | null = this.state.renamingProcessId;
    if (!processId || this.state.isRenameSaving) return;
    const target: IWorkFlowDto | undefined = this.state.processes.find(
      (process: IWorkFlowDto): boolean => process.id === processId
    );
    if (!target) {
      this.cancelRename();
      return;
    }

    const outcome: ProcessRenameOutcome = resolveProcessRename(target, this.state.renameValue, {
      workflows: this.props.sd.workFlowList,
      screens: this.props.sd?.screenList,
      datasources: this.props.sd?.datasources
    });
    if (outcome.kind === "unchanged") {
      this.cancelRename();
      return;
    }
    if (outcome.kind === "rejected") {
      // Keep the editor open on the rejected value so it can be corrected in place.
      KMainFunctions.displayWarningNotification(outcome.message);
      return;
    }

    this.setState({ isRenameSaving: true });
    this.markProcessBusy(processId, true);
    try {
      const payload: IWorkFlowDto = { name: outcome.name } as IWorkFlowDto;
      const res = await WorkFlowService.Update(processId, payload);
      if (res?.status === 200) {
        this.cancelRename();
        await this.refreshAfterMutation();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isRenameSaving: false });
      this.markProcessBusy(processId, false);
    }
  };

  handleRenameKeyDown = (e: React.KeyboardEvent): void => {
    // Keys must not leak into the table (row handlers, sort shortcuts) while typing.
    e.stopPropagation();
    if (e.key === "Enter") {
      void this.commitRename();
    } else if (e.key === "Escape") {
      this.cancelRename();
    }
  };

  renderRenameEditor = (): React.ReactNode => (
    // The editor replaces only the name text; the icon tile stays so the row doesn't jump.
    // Clicks inside must neither open the process nor bubble into the row's handlers.
    <span
      className="kuika-list-table__name-cell kuika-create-process__rename"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <span className="kuika-list-table__name-icon">
        <KuikaSvg name="tournament" width={16} height={16} />
      </span>
      <KuikaInput
        size="sm"
        autoFocus
        value={this.state.renameValue}
        disabled={this.state.isRenameSaving}
        onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
          this.setState({ renameValue: String(value ?? "") })
        }
        onKeyDown={this.handleRenameKeyDown}
        onBlur={() => void this.commitRename()}
        data-testid="createProcessRenameInput"
      />
    </span>
  );

  /**
   * Mirrors the Processes panel's delete side-effects: drop the process→screen
   * mappings, prune its start triggers, and forget it if it was the open one.
   */
  deleteProcess = async (): Promise<void> => {
    const target: IWorkFlowDto | undefined = this.state.processToDelete;
    if (!target || this.state.isRefreshing || this.isProcessBusy(target)) return;
    // The row stays on screen until the refetch drops it, so it reports progress the
    // same way a status toggle does.
    this.markProcessBusy(target.id, true);
    try {
      await deleteProcessEverywhere(this.props.sd, target);
      await this.refreshAfterMutation();
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.markProcessBusy(target.id, false);
    }
  };

  handleTabChange = (value: string): void => {
    // Sort keys differ between the process tabs and the template gallery, so a sort
    // that cannot exist on the incoming tab is dropped instead of silently ignored.
    // A half-typed rename doesn't survive the tab switch either — its row is gone.
    this.setState({
      pickedTab: value,
      processSort: null,
      templateSort: null,
      renamingProcessId: null,
      renameValue: ""
    });
    if (value === TEMPLATES_TAB && FeatureGates.isProcessTemplatesEnabled()) {
      void this.loadTemplates();
    }
  };

  /** Fetches the backend catalog once per mount; the tab shows a spinner meanwhile. */
  loadTemplates = async (): Promise<void> => {
    if (this.state.templatesLoaded || this.state.isTemplatesLoading) return;
    this.setState({ isTemplatesLoading: true });
    try {
      const res = await WorkFlowService.GetProcessTemplates();
      const rows: IProcessTemplate[] =
        res?.status === 200 && Array.isArray(res.data) ? toProcessTemplates(res.data) : [];
      this.setState({ templates: rows });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isTemplatesLoading: false, templatesLoaded: true });
    }
  };

  /**
   * A process's name comes from the template's ASCII seed (the Turkish display name
   * cannot pass the workflow-name rule): the seed itself when free, else the first
   * available "seed N".
   */
  buildTemplateProcessName = (template: IProcessTemplate): string => {
    for (let index = 0; index <= BLANK_PROCESS_MAX_ATTEMPTS; index++) {
      const candidate: string =
        index === 0 ? template.defaultProcessName : `${template.defaultProcessName} ${index + 1}`;
      const conflict = findWorkflowNameConflict({
        name: candidate,
        workflows: this.props.sd.workFlowList,
        screens: this.props.sd?.screenList,
        datasources: this.props.sd?.datasources,
        excludeWorkflowId: this.props.editingWfId
      });
      if (!conflict) return candidate;
    }
    // Exhausted: hand back the seed and let createProcess surface the conflict.
    return template.defaultProcessName;
  };

  handleUseTemplate = (template: IProcessTemplate): void => {
    if (this.state.isCreating) return;
    void this.createProcess(this.buildTemplateProcessName(template), template);
  };

  handleViewTemplateDetails = (template: IProcessTemplate): void => {
    this.setState({ templateToView: template });
  };

  handleCloseTemplateDetails = (): void => {
    this.setState({ templateToView: undefined });
  };

  /**
   * A successful "Use Template" leaves the gallery, so the modal closes first and the
   * outcome (the opened process, or a validation warning) is never hidden behind an
   * open dialog.
   */
  handleUseTemplateFromDetails = (template: IProcessTemplate): void => {
    this.setState({ templateToView: undefined });
    this.handleUseTemplate(template);
  };

  getProcessColumns(): KuikaListTableColumn<IWorkFlowDto>[] {
    return [
      {
        id: "name",
        header: () => "Name",
        accessorFn: (row: IWorkFlowDto) => row.displayName || row.name,
        cell: (row: IWorkFlowDto) =>
          this.state.renamingProcessId === row.id ? (
            this.renderRenameEditor()
          ) : (
            <KuikaListTableNameCell
              name={row.displayName || row.name}
              icon={<KuikaSvg name="tournament" width={16} height={16} />}
              secondary={row.description || undefined}
            />
          )
      },
      {
        id: "status",
        header: () => "Status",
        width: 120,
        accessorFn: (row: IWorkFlowDto) => (isProcessActive(row) ? 0 : 1),
        cell: (row: IWorkFlowDto) => {
          const active: boolean = isProcessActive(row);
          return (
            <KuikaListTableStatusCell
              label={active ? "Active" : "Inactive"}
              color={active ? "green" : "grey"}
              // Activate and Inactivate change this row's badge while the user watches it.
              animateChanges
              data-testid={`processRow-${processTestIdSlug(row.name)}-status`}
              hint={
                active
                  ? undefined
                  : "Won't be included next time this process is exported. Past exports that already included it aren't affected."
              }
            />
          );
        }
      },
      {
        id: "flow",
        header: () => "Flow",
        width: 200,
        enableSorting: false,
        cell: (row: IWorkFlowDto) => {
          const summary: IWorkflowListSummary = describeWorkflowListSummary(
            row.json,
            this.props.sd.project?.startProcessTriggers?.[row.id]
          );
          return <KuikaSmallText className="kuika-list-table__muted">{formatWorkflowFlowCell(summary)}</KuikaSmallText>;
        }
      },
      {
        id: "version",
        header: () => "Version",
        width: 100,
        // A real, stable count to sort by — unlike Flow, which mixes two facts into one cell.
        accessorFn: (row: IWorkFlowDto) => row.versions?.length ?? 0,
        cell: (row: IWorkFlowDto) => (
          <KuikaSmallText className="kuika-list-table__muted">
            {row.versions && row.versions.length > 0 ? `v${row.versions.length}` : "—"}
          </KuikaSmallText>
        )
      },
      {
        id: "lastUpdated",
        header: () => "Last Updated",
        width: 180,
        // `_updatedDate` (falls back to createdAt) via the shared timestamp seam.
        accessorFn: (row: IWorkFlowDto) => getProcessTimestamp(row) ?? 0,
        cell: (row: IWorkFlowDto) => {
          const timestamp: number | undefined = getProcessTimestamp(row);
          // Who and when in one cell, the way the other landings' Last modified draws it. Creation
          // is the first "update": the creator stands in until someone edits the process, the same
          // fallback the timestamp takes — so an untouched process reads as its author and its
          // creation, and an edited one as the editor and the edit.
          const name: string | undefined = row.updatedByName || row.createdByName;
          const photoUrl: string | undefined = row.updatedByName ? row.updatedByPhotoUrl : row.createdByPhotoUrl;
          return (
            <KuikaListTableUserCell
              caption={timestamp ? KMainFunctions.formatRelativeTime(new Date(timestamp)) : undefined}
              name={name || undefined}
              photoUrl={photoUrl || undefined}
              initials={getProcessCreatorInitials(name) || undefined}
            />
          );
        }
      }
      // No "Created by": Last Updated already falls back to the creator for a process nobody has
      // edited since, and a separate person column pushed Flow and Version off narrower windows.
    ];
  }

  getProcessRowActions(): KuikaListTableRowAction<IWorkFlowDto>[] {
    return buildProcessRowActions({
      onRename: (row: IWorkFlowDto) => this.startRename(row),
      onToggleActive: (row: IWorkFlowDto) => void this.handleToggleProcessStatus(row),
      onRequestDelete: (row: IWorkFlowDto) => this.setState({ processToDelete: row })
    });
  }

  renderToolbar(activeTab: string): JSX.Element {
    const isTemplatesTab: boolean = activeTab === TEMPLATES_TAB;
    const sortOptions: KuikaSelectboxOption[] = isTemplatesTab
      ? TEMPLATE_SORT_OPTIONS.map((option: ITemplateSortOption): KuikaSelectboxOption => ({
          value: option.value,
          label: option.label
        }))
      : PROCESS_SORT_OPTIONS.map((option: IProcessSortOption): KuikaSelectboxOption => ({
          value: option.value,
          label: option.label
        }));
    const filterOptions: KuikaSelectboxOption[] = isTemplatesTab
      ? collectProcessTags(this.state.templates).map((tag: string): KuikaSelectboxOption => ({
          value: tag,
          label: tag
        }))
      : STATUS_OPTIONS;

    const templatesEnabled: boolean = FeatureGates.isProcessTemplatesEnabled();
    // The tab always carries a pill: "Beta" while the gallery is live (it is a young
    // feature), "Coming soon" while the kill switch has it landing on a notice instead
    // of the cards. The gallery's search/filter row hides with the cards: filters over
    // a notice would be dead controls.
    const templatesTabOption: { value: string; label: React.ReactNode; title: string } = {
      value: TEMPLATES_TAB,
      title: templatesEnabled ? "Process templates are in beta" : "Process templates are coming soon",
      label: (
        <span className="kuika-create-process__tab-label">
          {TEMPLATES_TAB}
          <span className="kuika-create-process__tab-pill">{templatesEnabled ? "Beta" : "Coming soon"}</span>
        </span>
      )
    };
    const showFilterControls: boolean = !isTemplatesTab || templatesEnabled;

    return (
      <KuikaListPageToolbar
        tabs={
          <KuikaTabs
            size="lg"
            options={[
              // `label` is not optional in practice: KuikaTabs renders `icon || label`, so an
              // option carrying only a value draws an empty pill.
              {
                value: RECENTLY_MODIFIED_TAB,
                label: RECENTLY_MODIFIED_TAB,
                dataTestId: RECENTLY_MODIFIED_TAB_TEST_ID
              },
              { value: ALL_TAB, label: ALL_TAB, dataTestId: "allProcessesTab" },
              templatesTabOption
            ]}
            value={activeTab}
            onChange={this.handleTabChange}
            dataTestId="createProcessTabs"
          />
        }
        search={
          showFilterControls ? (
            <KuikaInput
              size="md"
              placeholder="Search..."
              value={this.state.search}
              leftIcon={<KuikaSvg name="search" width={14} height={14} />}
              onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
                this.handleSearchChange(String(value))
              }
              data-testid="createProcessSearch"
            />
          ) : undefined
        }
        filters={
          showFilterControls ? (
            <>
              <KuikaSelectbox
                className="kuika-create-process__filter-select"
                size="md"
                placeholder={isTemplatesTab ? "Tag" : "Status"}
                allowClear
                value={isTemplatesTab ? this.state.templateTag : this.state.statusFilter}
                options={filterOptions}
                prefix={<KuikaSvg name={isTemplatesTab ? "tag" : "filter"} width={12} height={12} />}
                onChange={(value: string | null) =>
                  isTemplatesTab
                    ? this.setState({ templateTag: value ?? null })
                    : this.setState({ statusFilter: (value as ProcessStatusFilter) ?? null })
                }
                data-testid="createProcessPrimaryFilter"
              />
              <KuikaSelectbox
                className="kuika-create-process__sort-select"
                size="md"
                placeholder="Sort by"
                allowClear
                value={isTemplatesTab ? this.state.templateSort : this.state.processSort}
                options={sortOptions}
                prefix={<KuikaSvg name="sort-descending" width={12} height={12} />}
                onChange={(value: string | null) =>
                  isTemplatesTab
                    ? this.setState({ templateSort: (value as TemplateSortKey) ?? null })
                    : this.setState({ processSort: (value as ProcessSortKey) ?? null })
                }
                data-testid="createProcessSortFilter"
              />
            </>
          ) : undefined
        }
      />
    );
  }

  renderTemplates(templates: IProcessTemplate[]) {
    // Gated off (kill switch): the tab lands on an honest notice instead of the gallery.
    if (!FeatureGates.isProcessTemplatesEnabled()) {
      return (
        <div className="kuika-create-process__state" data-testid="createProcessTemplatesComingSoon">
          <KuikaEmptyState
            variant="page"
            svgName="automation"
            customName="Process templates are coming soon"
            description="Ready-made processes you can start from — pick a template to get its diagram, then wire up the data and actions. We're preparing the first set."
          />
        </div>
      );
    }

    if (this.state.isTemplatesLoading) {
      // Card-shaped skeletons in the real grid (the shared loading primitive): the wait
      // previews the gallery it is fetching instead of a lone spinner.
      return (
        <div className="kuika-create-process__grid" aria-busy="true" data-testid="createProcessTemplatesLoading">
          {[0, 1, 2].map((index: number) => (
            <div key={index} className="kuika-create-process__card-skeleton">
              <KuikaSkeleton height={242} className="kuika-create-process__card-skeleton-cover" />
              <KuikaSkeleton width="55%" height={14} />
              <KuikaSkeleton width="35%" height={11} />
            </div>
          ))}
        </div>
      );
    }

    if (templates.length === 0) {
      const isFiltered: boolean = this.state.templates.length > 0;
      return (
        <div className="kuika-create-process__state">
          <KuikaEmptyState
            variant="page"
            svgName="automation"
            // customName, not name: `name` feeds the "No {name} added yet." template and
            // would double the "No" here.
            customName={isFiltered ? "No templates match your filters" : "No templates available"}
            description={
              isFiltered
                ? "Try a different search term, or clear the tag filter."
                : "The template catalog is empty right now — check back later."
            }
          />
        </div>
      );
    }

    return (
      <div className="kuika-create-process__grid" data-testid="createProcessTemplateGrid">
        {templates.map((template: IProcessTemplate) => (
          <CreateWorkflowTemplateCard
            key={template.id}
            template={template}
            onUse={this.handleUseTemplate}
            onViewDetails={this.handleViewTemplateDetails}
            isCreating={this.state.creatingTemplateId === template.id}
            disabled={this.state.isCreating && this.state.creatingTemplateId !== template.id}
          />
        ))}
      </div>
    );
  }

  /** The glyph over an empty list: what is filtering it, or what the tab is about. */
  renderEmptyIcon = (isRecentTab: boolean): React.ReactNode => {
    if (this.state.searchQuery) return <KuikaSvg name="search-off" width={32} height={32} />;
    if (isRecentTab) return <KuikaSvg name="history" width={32} height={32} />;
    return <KuikaSvg name="automation" width={32} height={32} />;
  };

  getEmptyTitle = (isRecentTab: boolean): string => {
    if (isRecentTab) return "Nothing changed recently";
    return "No processes yet";
  };

  getEmptyDescription = (isRecentTab: boolean): string => {
    if (isRecentTab) {
      // A failed feed is said out loud rather than shown as "nothing happened", which would be a
      // claim the page cannot actually make.
      return this.state.feed.status === "failed"
        ? "We couldn't load recent activity — open All Processes to see every process."
        : "Processes edited in the last 30 days appear here.";
    }
    return "Create a blank process or start from a template.";
  };

  renderProcessTable(isRecentTab: boolean): JSX.Element {
    const filteredRows: IWorkFlowDto[] = filterProcesses(
      this.state.processes,
      this.state.searchQuery,
      this.state.statusFilter,
      this.state.processSort
    );
    const rows: IWorkFlowDto[] = isRecentTab ? this.getRecentProcesses(filteredRows) : filteredRows;
    // The page parks on the recent tab while the feed is in flight. Without this it would paint
    // an empty table first and claim nothing had changed recently — something it cannot yet know.
    const isFeedPending: boolean = isRecentTab && this.state.feed.status === "loading";

    return (
      <KuikaListTable<IWorkFlowDto>
        data={rows}
        columns={this.getProcessColumns()}
        rowActions={this.getProcessRowActions()}
        isRowBusy={this.isProcessBusy}
        rowKey={(row: IWorkFlowDto): string => row.id}
        loading={this.state.isProcessesLoading || (isFeedPending && rows.length === 0)}
        onRowClick={this.handleOpenProcess}
        // The gallery is the only process-management surface left (KOA-15840 removed the
        // Processes left panel), so each row and each of its actions has to be addressable by
        // the process it belongs to.
        rowTestId={(row: IWorkFlowDto): string => `processRow-${processTestIdSlug(row.name)}`}
        emptyVariant="page"
        emptyIcon={this.renderEmptyIcon(isRecentTab)}
        emptyTitle={this.getEmptyTitle(isRecentTab)}
        emptyDescription={this.getEmptyDescription(isRecentTab)}
        data-testid="createProcessTable"
      />
    );
  }

  render() {
    const activeTab: string = this.getActiveTab();
    const isTemplatesTab: boolean = activeTab === TEMPLATES_TAB;
    const isRecentTab: boolean = activeTab === RECENTLY_MODIFIED_TAB;

    const templates: IProcessTemplate[] = filterProcessTemplates(
      this.state.templates,
      this.state.searchQuery,
      this.state.templateTag,
      this.state.templateSort
    );

    return (
      // A silent refresh has no visual spinner, so this is the only signal assistive
      // tech gets that the rows are being brought up to date.
      <KuikaListPage
        fill
        className="kuika-create-process"
        busy={this.state.isRefreshing}
        data-testid="createProcessScreen"
      >
        {/* Titled after the page's dominant job — managing existing processes — not the
            create card, which stays as one element within it. */}
        <KuikaListPageHeader
          icon={<KuikaSvg name="process-automation" type="topbar-icons" width={20} height={20} />}
          title="Process Automation"
          titleTestId="createProcessTitle"
        />

        <KuikaCreateCardRow>
          <KuikaCreateCard
            title="Create a blank"
            description="Start from an empty diagram with a start event, one sample step, and an end event."
            icon={<KuikaSvg name="plus" width={20} height={20} />}
            iconBackground="var(--blue-1)"
            iconColor="var(--accent-fg)"
            onClick={this.handleCreateBlank}
            // Spins only for a blank process; while a template's is being created it is merely
            // unavailable, and the pressed template card carries the spinner.
            loading={this.state.isCreating && !this.state.creatingTemplateId}
            disabled={this.state.isCreating}
            data-testid="createProcessBlankCard"
          />
          {/* The gallery already sat behind a tab; this is the card that says so, the way
              the Email Builder and Scheduled Jobs landings do. It routes through
              `handleTabChange` rather than setting the tab directly, because that is what
              fetches the catalog. Hidden while the kill switch is off — the tab then lands
              on a coming-soon notice, and a card leading to one is a dead end. */}
          {FeatureGates.isProcessTemplatesEnabled() && (
            <KuikaCreateCard
              title="From template"
              description="Start from a ready-made process — approvals, onboarding, request handling and more."
              icon={<KuikaSvg name="template" width={20} height={20} />}
              iconBackground="var(--purple-1)"
              iconColor="var(--purple-5)"
              onClick={() => this.handleTabChange(TEMPLATES_TAB)}
              data-testid="createProcessFromTemplateCard"
            />
          )}
        </KuikaCreateCardRow>

        {this.renderToolbar(activeTab)}

        {/* Benefit-led intro, BELOW the tabs on purpose — the old caveat banner sat above
            them and shifted the whole toolbar down on tab change. The caveat ("you wire up
            the bindings yourself") lives at the point of action, in the details modal. */}
        {isTemplatesTab && FeatureGates.isProcessTemplatesEnabled() && (
          <div className="kuika-create-process__templates-banner">
            <KuikaInfoBox
              color="blue"
              leftUnit={
                <span className="kuika-create-process__templates-banner-text">
                  <KuikaSvg name="info-circle" width={14} height={14} />
                  <KuikaSmallText>
                    Start from a proven process — each template gives you a ready diagram, and you wire up the data,
                    parameters, and actions next.
                  </KuikaSmallText>
                </span>
              }
            />
          </div>
        )}

        {isTemplatesTab ? this.renderTemplates(templates) : this.renderProcessTable(isRecentTab)}

        <CreateWorkflowTemplateDetailsModal
          template={this.state.templateToView}
          onClose={this.handleCloseTemplateDetails}
          onUseTemplate={this.handleUseTemplateFromDetails}
        />

        <DeleteModal
          itemName={this.state.processToDelete?.displayName || this.state.processToDelete?.name || ""}
          deleteType="Process"
          triggerModal={!!this.state.processToDelete}
          closeModal={() => this.setState({ processToDelete: undefined })}
          deleteItem={this.deleteProcess}
        />
      </KuikaListPage>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const createWorkflow = withSD<ICreateWorkflowProps>(CreateWorkflow);
export { createWorkflow as CreateWorkflow };
